import { ReactElement, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Bin,
  ChannelIcon,
  EllipsisMenu,
  IOption,
  FormatInformation,
  PriceBandIcon,
  PricingIcon,
  Prompt,
  Typography,
  SnackbarStatus,
  useSnackbar,
} from '@weareopenr/component-library';
import { IMenuItem, IOptionGroup } from 'src/types/menu.types';
import styles from './ModifierCategory.module.scss';
import { deleteOptionGroup } from 'src/api/deleteOptionGroup/deleteOptionGroup';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';
import Modifier from '../Modifier';
import { capitalise } from 'src/helpers/capitalise';
import Burger from 'src/assets/images/Burger';
import EmptyState from '../EmptyState';
import { FEATURE_FLAGS, ItemType, OptionGroupType } from '../../constants';
import { useAuthWrapper } from '@weareopenr/authkit';
import { assignOptionGroupToItem } from '../../api/assignOptionGroupToItem';

interface ModifierCategoryProps {
  optionGroup: IOptionGroup;
  index: number;
  openOptionGroupModal: (groupToEdit?: IOptionGroup) => void;
  openOptionGroupPricingModal: (groupToEdit?: IOptionGroup) => void;
  openOptionGroupExternalSystemsModal: (groupToEdit: IOptionGroup) => void;
  item?: IMenuItem;
}

const selectionCriteria = (min?: number, max?: number): string => {
  if (min && max) return `Min of ${min} and Max of ${max}`;
  else if (min) return `Min of ${min}`;
  else if (max) return `Max of ${max}`;
  else return 'None';
};

export const ModifierCategory = ({
  optionGroup,
  index,
  openOptionGroupModal,
  openOptionGroupPricingModal,
  openOptionGroupExternalSystemsModal,
  item,
}: ModifierCategoryProps): ReactElement => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { featureFlags } = useAuthWrapper();
  const independentOptionGroups = featureFlags?.includes(FEATURE_FLAGS.INDEPENDENT_OPTION_GROUPS) ?? false;
  const isStandalone = item === undefined;
  const isBundle = item ? item.type === ItemType.BUNDLE : optionGroup.type === OptionGroupType.BUNDLE;
  const isMovable = !isStandalone;
  const [groupAction, groupActionPast, groupActionPresent] = independentOptionGroups
    ? ['Remove', 'removed', 'removing']
    : ['Delete', 'deleted', 'deleting'];

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const externalSystems =
    optionGroup.external_systems.map((system) => system.name).join(', ') || 'No channels selected';

  const handleDeleteOptionGroup = async (): Promise<void> => {
    if (!item) return;
    const { itemId: currentItemId, optionGroup: allOptionGroups } = item;
    const remainingOptionGroups = [...allOptionGroups.slice(0, index), ...allOptionGroups.slice(index + 1)];

    setIsDeleting(true);
    try {
      if (independentOptionGroups) {
        await assignOptionGroupToItem(
          currentItemId,
          remainingOptionGroups.map((optionGroup) => optionGroup.optionGroupId),
        );
      } else {
        await deleteOptionGroup(optionGroup.optionGroupId);
      }
      queryClient.setQueriesData<IMenuItem>([QueryKeys.item, currentItemId], (item) =>
        item ? { ...item, optionGroup: remainingOptionGroups } : item,
      );
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully ${groupActionPast} ${isBundle ? 'bundle group' : 'modifier group'} ${optionGroup.name}.`,
      });
      setShowDeleteModal(false);
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: `Something went wrong whilst ${groupActionPresent} ${isBundle ? 'bundle group' : 'modifier group'} ${optionGroup.name}.`,
      });
    } finally {
      setIsDeleting(false);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.item, currentItemId] });
    }
  };

  const moveOptionGroup = async (from: number, to: number) => {
    if (!item) return;
    const { itemId: currentItemId, optionGroup: allOptionGroups } = item;
    const reorderedGroups = [...allOptionGroups];
    [reorderedGroups[from], reorderedGroups[to]] = [reorderedGroups[to], reorderedGroups[from]];

    try {
      queryClient.setQueriesData<IMenuItem>([QueryKeys.item, currentItemId], (item) =>
        item ? { ...item, optionGroup: reorderedGroups } : item,
      );
      await assignOptionGroupToItem(
        currentItemId,
        reorderedGroups.map((group) => group.optionGroupId),
      );
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: `Something went wrong whilst moving ${isBundle ? 'bundle group' : 'modifier group'}.`,
      });
    } finally {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.item, currentItemId] });
    }
  };

  const canDelete = !isStandalone && (!isBundle || item.optionGroup.length > 1);

  const options: IOption[] = [
    {
      id: 'edit-details',
      name: 'Edit Option Group',
      onOptionClick: () => openOptionGroupModal(optionGroup),
      icon: PriceBandIcon,
    },
    ...(isBundle
      ? []
      : [
          {
            id: 'edit-external-systems',
            name: 'Edit Publish Channels',
            onOptionClick: () => openOptionGroupExternalSystemsModal(optionGroup),
            icon: ChannelIcon,
          },
        ]),
    {
      id: 'edit-prices',
      name: 'Edit Prices',
      onOptionClick: () => openOptionGroupPricingModal(optionGroup),
      icon: PricingIcon,
    },
    ...(isMovable
      ? [
          {
            id: 'up',
            name: 'Move up',
            icon: ArrowUpIcon,
            onOptionClick: () => moveOptionGroup(index, index - 1),
            isDisabled: index === 0,
            dataTestId: 'move-up-button',
          },
          {
            id: 'down',
            name: 'Move down',
            icon: ArrowDownIcon,
            onOptionClick: () => moveOptionGroup(index, index + 1),
            isDisabled: item.optionGroup.length === index + 1,
            dataTestId: 'move-down-button',
          },
        ]
      : []),
    ...(isStandalone
      ? []
      : ([
          { id: 'delete-divider', isDivider: true },
          {
            id: 'delete',
            name: groupAction,
            icon: Bin,
            onOptionClick: () => setShowDeleteModal(true),
            hoverColour: 'tomato',
            dataTestId: 'delete-button',
            isDisabled: !canDelete,
          },
        ] satisfies IOption[])),
  ];

  return (
    <>
      <Prompt
        promptHeading={`${groupAction} ${isBundle ? 'bundle' : 'modifier'} group?`}
        closePrompt={() => setShowDeleteModal(false)}
        open={showDeleteModal}
        buttonHandler={handleDeleteOptionGroup}
        buttonText={groupAction}
        buttonColour="tomato"
        backButtonText="Cancel"
        backButton={true}
        size="small"
        isSubmitting={isDeleting}
      >
        {independentOptionGroups
          ? 'This action will remove the link between product and the group. The group will remain active in the product library'
          : 'This action cannot be undone.'}
      </Prompt>

      <div className={styles['container']} key={optionGroup.optionGroupId}>
        <div className={styles['information-container']}>
          <div className={styles['info-column']}>
            {isStandalone && (
              <FormatInformation heading="Option Group Type">
                {optionGroup.type ? capitalise(optionGroup.type) : '-'}
              </FormatInformation>
            )}
            <FormatInformation heading="Name">{optionGroup.name}</FormatInformation>
            <FormatInformation heading="Consumer Name">{optionGroup.consumer_name || '-'} </FormatInformation>
            {optionGroup.plu !== undefined && (
              <FormatInformation heading="Group POS ID">{optionGroup.plu || '-'}</FormatInformation>
            )}
            {!isBundle && <FormatInformation heading="Description">{optionGroup.description}</FormatInformation>}
          </div>
          <div className={styles['info-column']}>
            <FormatInformation heading={'Range of choices'}>
              {selectionCriteria(optionGroup.min, optionGroup.max)}
            </FormatInformation>
            {!isBundle && <FormatInformation heading="Publishing channels">{externalSystems}</FormatInformation>}
          </div>
          <EllipsisMenu data-testid="menu-options-button" options={options} variant="ghost" />
        </div>

        <div>
          <Typography size="medium" weight="bold">
            {isBundle ? 'Products' : 'Modifiers'}
          </Typography>
          <div className={styles['modifiers-container']}>
            {optionGroup.items.length > 0 ? (
              optionGroup.items.map(
                ({ optionItemId, name, valueWithTax, posId, inheritPrice, min, max, default: defaultVal }) => (
                  <Modifier
                    key={optionItemId}
                    name={name}
                    price={valueWithTax}
                    inheritPrice={inheritPrice}
                    posId={posId}
                    link={`/items/${optionItemId}`}
                    min={min}
                    max={max}
                    default={defaultVal}
                  />
                ),
              )
            ) : (
              <EmptyState
                Icon={Burger}
                emptySectionText={
                  isBundle
                    ? 'No products have been added to this option group'
                    : 'No modifiers have been added to this option group'
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ModifierCategory;
